'use client'
import * as Sentry from '@sentry/react'
import AudioContainer, { AudioEngine, GlobalAudioPlayer } from '@tunasong/audio-ui'
import { createRTKReduxGraph } from '@tunasong/graph-lib'
import { logger, shortUuid } from '@tunasong/models'
import { useRegisterWebPush } from '@tunasong/notifications'
import type { ExecuteAIPrompt } from '@tunasong/plugin-lib'
import { GlobalAppContext } from '@tunasong/plugin-lib'
import { ConfigService, nlpApi, useStore, useThunkDispatch } from '@tunasong/redux'
import { EntityActionsMenu, ErrorGeneral, MovePickerHost, ReleaseNotesDialog } from '@tunasong/tunasong-ui'
import { type UIExtensions } from '@tunasong/ui-lib'
import {
  BandPinHost,
  NotificationAlert,
  TunaTheme,
  UIExtensionsContext,
  UploadContext,
  UploadManager,
  useAsyncUserConfigService,
  useDeviceInfo,
  useFormDialog,
  useGlobalFormDialog,
  useNavigateToEntity,
  useTunaColorScheme,
  useTunaMediaQuery,
  useWakeLock,
} from '@tunasong/ui-lib'
import { VendorServices } from '@tunasong/vendor'
import { WebSocketContext, useReceiveRedux } from '@tunasong/ws'
import React, { use, useEffect, useMemo, useRef } from 'react'
import { DndProvider } from 'react-dnd'
import { HTML5Backend } from 'react-dnd-html5-backend'
import { TouchBackend } from 'react-dnd-touch-backend'
import type { $ZodType } from 'zod/v4/core'
import TunaGlobalStyles from './app-global-styles.js'
import { AuthGuard } from './auth/index.js'
import { DIContainer } from './di/di-container.js'
import { FullPageLayout } from './di/layouts/full-page.js'
import { Repository } from './di/plugin-repository.js'
import { GlobalCommandHotkeys } from './hooks/global-command-hotkeys.component.js'
import { useGlobalHotkeys } from './hooks/global-hotkeys.js'
import { useSWSync } from './sw/sw-sync.js'
import { useCreateWebsocket } from './ws/create-websocket.hook.js'
import { DynamicCSP } from './dynamic-csp.js'
import { SEO } from './components/seo.js'

export interface AppProps {
  children?: React.ReactNode
  component: React.FunctionComponent<Record<string, unknown>>
  /** The route requires an authenticated user*/
  auth?: boolean
  /** The route requires the Audio mixer set up. @default false */
  audio?: boolean
  /** Use the specified theme. @default is to use the user's theme */
  theme?: 'light' | 'dark'
}

const configService = new ConfigService()
const audioEngine = new AudioEngine({ configService })

/**
 * Initialize the user-config service exactly once for the app.
 * `useUserConfig` intentionally no longer ensures initialization (GH #1548) —
 * it is called by leaf components (e.g. every chord chip via the theory
 * hooks) and must stay subscription-light. Mounted inside
 * GlobalAppContext.Provider because the underlying hook reads the config
 * service from the app context.
 */
const UserConfigBootstrap: React.FC = () => {
  useAsyncUserConfigService()
  return null
}

/**
 * The plugin set is loaded on first render, not at module scope: router.tsx imports this module
 * statically, so a module-scope load fired every plugin import from the entry chunk, even on
 * routes that never mount an AppContainer (GH #1914).
 */
const pluginRepository = new Repository()

export const AppContainer: React.FC<AppProps> = ({
  auth = true,
  component: Component,
  theme,
  children,
  /** Not forwarded to the page component - the audio engine is always provided below */
  audio: _audio,
  ...restProps
}) => {
  /** Global styles */

  const appContainerId = useRef(shortUuid())

  useEffect(() => {
    const containerId = appContainerId.current
    logger.debug(`AppContainer ${containerId} mounted`)
    return () => {
      logger.debug(`AppContainer ${containerId} unmounted`)
    }
  }, [])

  /** Register meta keys.  You can use this by adding data-meta-pressed to the body */
  useEffect(() => {
    const handlePressed = (e: KeyboardEvent) => {
      if (e.metaKey || e.altKey) {
        document.body.setAttribute('data-meta-pressed', 'true')
      }
    }
    const handleDepressed = (e: KeyboardEvent) => {
      if (!e.metaKey && !e.altKey) {
        document.body.removeAttribute('data-meta-pressed')
      }
    }
    document.addEventListener('keydown', handlePressed)
    document.addEventListener('keyup', handleDepressed)
    return () => {
      document.removeEventListener('keydown', handlePressed)
      document.removeEventListener('keyup', handleDepressed)
    }
  }, [])

  const { isIPad, isMobile } = useDeviceInfo()

  /** Receive messages from Service Worker */
  useSWSync()

  const { globalHotkeyModals } = useGlobalHotkeys()

  /** Ensure the mobile page does not lock. We gate on `(pointer: coarse)` in
   *  addition to UA-based detection — Macbooks always report `pointer: fine`
   *  even when `maxTouchPoints` or the UA is misreported (Sidecar, accessibility
   *  features, browser dev tools), so this prevents the screen wake lock from
   *  also blocking the laptop from sleeping with the lid closed (GH #1380). */
  const isCoarsePointer = useTunaMediaQuery('(pointer: coarse)')
  useWakeLock((isMobile || isIPad) && isCoarsePointer)

  /** Request notifications */
  useRegisterWebPush()

  /** Websocket */
  const webSocket = useCreateWebsocket()

  /** Handle redux messages over the websocket */
  useReceiveRedux(webSocket)

  // App specific global App context
  const { getState, subscribe } = useStore()
  const dispatch = useThunkDispatch()
  const navigateToEntity = useNavigateToEntity()
  const prompt = useFormDialog()

  /** Memoized until it settles; a failed load is retried on the next render (GH #1914) */
  const plugins = use(pluginRepository.loadCollection('all'))

  // Upload manager
  const uploadManager = useMemo(
    () =>
      new UploadManager({
        dbName: 'entity-uploads',
        dispatch,
        getState,
        plugins,
      }),
    [dispatch, getState, plugins]
  )

  const AuthContainer = auth ? AuthGuard : React.Fragment

  const { colorScheme: systemScheme } = useTunaColorScheme()
  const colorScheme = theme ?? systemScheme

  const graph = useMemo(() => createRTKReduxGraph({ dispatch }), [dispatch])

  const executeAIPrompt: ExecuteAIPrompt = async ({ messages }) => {
    const result = await dispatch(nlpApi.endpoints.complete.initiate({ prompt: messages })).unwrap()
    return { text: result.text, toolResults: [] }
  }

  const globalAppContext = {
    engine: audioEngine,
    configService,
    /** UIContext extensions for app. These can use components in app, and be surfaced in the standard components. */
    uiExtensions: {
      entityList: {
        ItemMenu: EntityActionsMenu,
      },
    } satisfies UIExtensions,
    getState,
    dispatch,
    subscribe,
    navigateToEntity,
    graph,
    executeAIPrompt,
    prompt: <T extends Record<string, unknown>>({
      schema,
      title,
      defaultValues,
      description,
      okText,
    }: {
      schema: $ZodType<T>
      title: string
      defaultValues?: Record<string, unknown>
      description?: string
      okText?: string
    }) =>
      prompt({
        schema,
        defaultValues: defaultValues as never,
        dialogProps: {
          title,
          description,
          okText,
        },
      }),
  }

  const { formDialog } = useGlobalFormDialog()

  const { dndBackend, dndOptions } = useMemo(() => {
    if (isMobile || isIPad) {
      return {
        dndBackend: TouchBackend,
        dndOptions: {
          enableMouseEvents: true,
          delayTouchStart: 200,
        },
      }
    }
    return { dndBackend: HTML5Backend, dndOptions: {} }
  }, [isIPad, isMobile])

  return (
    <Sentry.ErrorBoundary fallback={ErrorGeneral}>
      <GlobalAppContext.Provider value={globalAppContext}>
        <UploadContext.Provider value={uploadManager}>
          <VendorServices>
            <UIExtensionsContext.Provider value={globalAppContext?.uiExtensions ?? null}>
              <TunaTheme colorScheme={colorScheme}>
                <TunaGlobalStyles />
                <SEO title="Tunasong" />
                <DynamicCSP />

                <FullPageLayout>
                  <WebSocketContext.Provider value={webSocket}>
                    <DIContainer pluginRepository={pluginRepository}>
                      {/* Dispatch user-configurable global commands (e.g. agent-toggle-panel) on
                          document keydown. Mounted inside GlobalAppContext.Provider + DIContainer
                          because the underlying hook reads from both. */}
                      <GlobalCommandHotkeys />
                      <UserConfigBootstrap />
                      <DndProvider backend={dndBackend} options={dndOptions}>
                        <AuthContainer>
                          <ReleaseNotesDialog />
                          <NotificationAlert />
                          {/* Move… opens it from any menu; mounted once so its Undo toast outlives the menu (#2543) */}
                          <MovePickerHost />
                          {/* Pin to {Band}: its toasts and Undo outlive the menu that asked (GH #2822) */}
                          <BandPinHost />
                          <AudioContainer engine={globalAppContext?.engine ?? null}>
                            <GlobalAudioPlayer key={`${appContainerId}-global-player`} />
                            <Component {...restProps} />
                            {/* Prompt / form dialog */}
                            {formDialog}
                            {globalHotkeyModals}
                            {children}
                          </AudioContainer>
                        </AuthContainer>
                      </DndProvider>
                    </DIContainer>
                  </WebSocketContext.Provider>
                </FullPageLayout>
              </TunaTheme>
            </UIExtensionsContext.Provider>
          </VendorServices>
        </UploadContext.Provider>
      </GlobalAppContext.Provider>
    </Sentry.ErrorBoundary>
  )
}

export default AppContainer
